// GroupsBulkActions — the Groups tab's selection-bar action cluster: a
// "Move to section" Dropdown listing the named sections, a direct
// "Move to unassigned" for the default section, and Delete. Presentational —
// GroupsTab owns the flows (and the looping, since there's no bulk endpoint).
// Every callback is OPTIONAL: the page passes `undefined` when the verb is
// missing and that button doesn't render — absence means hidden.

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dropdown, Icon } from '@/components/common';
import type { GroupSection } from '@/api/endpoints/groupsApi';

interface GroupsBulkActionsProps {
  /** Named (non-default) sections — the Move-to menu's rows. */
  sections: GroupSection[];
  /** False when every selected group already sits in the default section. */
  canMoveToUnassigned: boolean;
  /** Absent = hidden. */
  onMoveToSection?: (sectionId: string) => void;
  /** Absent = hidden. */
  onMoveToUnassigned?: () => void;
  /** Absent = hidden. */
  onDelete?: () => void;
}

const GroupsBulkActions = ({
  sections,
  canMoveToUnassigned,
  onMoveToSection,
  onMoveToUnassigned,
  onDelete,
}: GroupsBulkActionsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Close before running, so the menu isn't left hanging over the confirm.
  const pick = (run: () => void) => {
    setOpen(false);
    run();
  };

  const row = (section: GroupSection): ReactNode => (
    <button
      key={section.id}
      type='button'
      className='app-dropdown__item'
      role='menuitem'
      onClick={() => pick(() => onMoveToSection?.(section.id))}
    >
      <Icon name='folder' size={15} />
      <span className='app-dropdown__item-label'>{section.name}</span>
    </button>
  );

  return (
    <>
      {onMoveToSection && (
        <Dropdown
          open={open}
          onClose={() => setOpen(false)}
          align='start'
          width={240}
          portal
          trigger={
            <Button
              variant='ghost'
              size='sm'
              iconStart='folder-input'
              iconEnd={open ? 'chevron-up' : 'chevron-down'}
              aria-haspopup='menu'
              aria-expanded={open}
              disabled={sections.length === 0}
              onClick={() => setOpen((o) => !o)}
            >
              {t('groups_action_move')}
            </Button>
          }
        >
          <div className='app-dropdown__header groups-menu__head'>{t('groups_move_to_header')}</div>
          {sections.map(row)}
        </Dropdown>
      )}

      {onMoveToUnassigned && (
        <Button
          variant='ghost'
          size='sm'
          iconStart='inbox'
          disabled={!canMoveToUnassigned}
          onClick={onMoveToUnassigned}
        >
          {t('groups_action_move_unassigned')}
        </Button>
      )}

      {onDelete && (
        <Button
          variant='ghost'
          size='sm'
          iconStart='trash-2'
          className='groups-tab__danger-action'
          onClick={onDelete}
        >
          {t('groups_action_delete')}
        </Button>
      )}
    </>
  );
};

export default GroupsBulkActions;
